import { useMutation, useQuery } from '@tanstack/react-query';

import { comparePeriods } from '@hookpipe/contracts/retention/retention-period';
import { Button } from '../../shared/ui/button';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { ClockIcon } from '../../shared/ui/icons';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  formatBytes,
  periodLabel,
  retentionImpact,
  setDefaultPeriod,
  setFolderPeriod,
  useRefreshStorage,
  warningText,
  type TRetentionImpact,
  type TRetentionPeriod,
} from '../../entities/retention';

export type TImpactTarget =
  | { level: 'organization' }
  | { level: 'folder'; id: string; name: string; parentName: string | null };

interface IImpactDialogProps {
  target: TImpactTarget;
  /** The new period; null lets a folder follow the one above it again. */
  period: TRetentionPeriod | null;
  /** What the followers keep now. */
  from: TRetentionPeriod;
  onClose: () => void;
}

const count = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const removalWords = (impact: TRetentionImpact, from: TRetentionPeriod): string => {
  if (impact.removeTonightBytes > impact.removeTonightBeforeBytes) {
    return `~${formatBytes(impact.removeTonightBytes)} older than ${periodLabel(impact.period)}`;
  }
  return comparePeriods(impact.period, from) > 0
    ? 'nothing — they start keeping more from tonight'
    : `nothing older than ${periodLabel(impact.period)} yet`;
};

const Facts = ({
  target,
  impact,
  from,
}: {
  target: TImpactTarget;
  impact: TRetentionImpact;
  from: TRetentionPeriod;
}) => {
  const folders = impact.keepOwn.filter((keep) => keep.kind === 'folder');
  const flows = impact.keepOwn.filter((keep) => keep.kind === 'flow');
  const followers = [...impact.followerFolders, ...(impact.unfiled ? ['not in a folder'] : [])];

  if (target.level === 'organization') {
    const unchanged = [
      folders.map((folder) => `${folder.name} ${periodLabel(folder.period)}`).join(', '),
      flows.length > 0 ? count(flows.length, 'exception') : '',
    ].filter(Boolean);
    return (
      <FactList>
        <FactRow label="Follow the default">
          {[followers.join(', '), count(impact.followerFlows, 'flow')].filter(Boolean).join(' · ')}
        </FactRow>
        <FactRow label="Unchanged">
          {unchanged.length > 0 ? unchanged.join(' · ') : 'nothing'}
        </FactRow>
        <FactRow label="Deleted">{removalWords(impact, from)}</FactRow>
      </FactList>
    );
  }
  return (
    <FactList>
      <FactRow label="Follow the folder">
        {count(impact.followerFlows, 'flow')}
        {impact.followerFolders.length > 0
          ? ` · ${impact.followerFolders.join(', ')} included`
          : ''}
      </FactRow>
      <FactRow label="Keep their own period">
        {impact.keepOwn.length > 0
          ? impact.keepOwn.map((keep) => `${keep.name} · ${periodLabel(keep.period)}`).join(', ')
          : 'none'}
      </FactRow>
      <FactRow label="Tonight’s cleanup would remove">{removalWords(impact, from)}</FactRow>
    </FactList>
  );
};

/** Board S2: before a folder's or the organization's period changes, what it does. */
export const ImpactDialog = ({ target, period, from, onClose }: IImpactDialogProps) => {
  const refresh = useRefreshStorage();
  const impact = useQuery({
    queryKey: [
      'retention',
      'impact',
      target.level,
      target.level === 'folder' ? target.id : '',
      period,
    ],
    queryFn: () =>
      retentionImpact({
        level: target.level,
        ...(target.level === 'folder' ? { id: target.id } : {}),
        period,
      }),
  });
  const save = useMutation({
    mutationFn: async (keep: string[]): Promise<void> => {
      if (target.level === 'folder') await setFolderPeriod(target.id, period, keep);
      else await setDefaultPeriod(period ?? from, keep);
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });

  const data = impact.data;
  const warnings = data?.warnings ?? [];
  const label = data ? periodLabel(data.period) : period ? periodLabel(period) : '';
  const title =
    target.level === 'organization'
      ? `Change the default to ${label}?`
      : period === null
        ? `Let ${target.name} follow ${target.parentName ?? 'the default'} again?`
        : `Set ${target.name} to ${label}?`;
  const description =
    target.level === 'organization'
      ? 'Only what follows the default changes. Folders with their own period and flow exceptions stay as they are.'
      : `Flows that follow ${target.name}${
          data && data.followerFolders.length > 0
            ? `, and its ${data.followerFolders.length === 1 ? 'subfolder' : 'subfolders'} ${data.followerFolders.join(', ')},`
            : ''
        } take ${label}. Exceptions keep their own period.`;
  const keep = warnings.map((warning) => warning.flowId);
  const submitLabel =
    target.level === 'organization'
      ? `Set the default to ${label}`
      : period === null
        ? `Follow ${target.parentName ?? 'the default'}`
        : `Set ${label}`;

  return (
    <Modal
      open
      width="md"
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
      footer={
        warnings.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={save.isPending}
            onClick={() => save.mutate([])}
          >
            {submitLabel} for all
          </Button>
        ) : undefined
      }
      submit={{
        label:
          warnings.length > 0
            ? `${submitLabel} · ${count(warnings.length, 'exception')}`
            : submitLabel,
        onSubmit: () => save.mutate(keep),
        disabled: !data,
        waiting: save.isPending,
        waitingLabel: 'Saving',
      }}
    >
      {impact.isPending && <LoadingState rows={3} />}
      {impact.isError && <ErrorState error={impact.error} onRetry={() => void impact.refetch()} />}
      {data && <Facts target={target} impact={data} from={from} />}
      {warnings.length > 0 && (
        <Note tone="caution" className="mt-3 flex flex-col gap-2">
          {warnings.map((warning) => (
            <span key={warning.flowId} className="flex gap-2">
              <ClockIcon size="sm" className="mt-hair text-muted" />
              <span>{warningText({ ...warning, stepName: warning.name }, data!.period)}</span>
            </span>
          ))}
          <span className="pl-5 text-muted">
            {warnings.length === 1
              ? `It stays on ${periodLabel(warnings[0]!.keeps)} as an exception, so a copy that gives up can still be redelivered.`
              : 'They stay on their current period as exceptions, so a copy that gives up can still be redelivered.'}
          </span>
        </Note>
      )}
      <MutationError error={save.error} />
    </Modal>
  );
};
